import React from "react";
import { useTranslation } from "react-i18next";
import { Button } from "react-bootstrap";
import { RequestProgressBar } from "../../commons/requestProgressBar/requestProgressBar";
import { RequestProgressBarStepModel } from "../../commons/requestProgressBar/requestProgressBarModel";
import { ContentHeader } from "../../../../general/modules/commons/content/contentHeader";
import { LayoutContent } from "../../../../general/modules/commons/layout/layout";
import { Content } from "../../../../general/modules/commons/content/content";
import { RequestResponseModel } from "../../../domain/models/requestModel";
import DatePicker, { DatePickerProps } from "react-datepicker";
import "react-datepicker/dist/react-datepicker.css";
import { useAuthorization } from "../../../../../hooks/useAuthorization";

interface ActiveRequestInfo {
  pickup: RequestResponseModel | null;
  delivery: RequestResponseModel | null;
  hasActiveRequest: boolean;
  activeRequest: RequestResponseModel | null;
  activeRequestId: number | undefined;
}

interface ActiveRequestManagementProps {
  requestType: "deliver" | "pickup" | null;
  activeRequest: ActiveRequestInfo | null;
  isEditingActiveRequest: boolean;
  editingValues: {
    workOrderNumber: string;
    pickupDeliveryDate: string;
  };
  onEditingToggle: () => void;
  onCancelActiveRequest: () => void;
  onEditRequestSubmit: () => void;
  onEditingValuesChange: (values: {
    workOrderNumber: string;
    pickupDeliveryDate: string;
  }) => void;
  datePickerOptions?: Partial<DatePickerProps>;
}

export const ActiveRequestManagement: React.FC<
  ActiveRequestManagementProps
> = ({
  requestType,
  activeRequest,
  isEditingActiveRequest,
  editingValues,
  onEditingToggle,
  onCancelActiveRequest,
  onEditRequestSubmit,
  onEditingValuesChange,
  datePickerOptions,
}) => {
  const { t } = useTranslation("common");
  const { hasRole } = useAuthorization();

  if (!activeRequest?.hasActiveRequest) return null;

  const filterDate = datePickerOptions?.filterDate;
  const monthsShown = datePickerOptions?.monthsShown;
  const minDate = datePickerOptions?.minDate;
  const maxDate = datePickerOptions?.maxDate;
  const highlightDates = datePickerOptions?.highlightDates;
  const excludeDates = datePickerOptions?.excludeDates;

  return (
    <div>
      <div className="alert alert-warning mt-3" role="alert">
        <div className="d-flex align-items-center">
          <i className="fas fa-exclamation-triangle me-2"></i>
          <div className="flex-grow-1">
            <strong>{t("Active request found")}</strong>
            <div>
              {activeRequest.pickup &&
                t(
                  "There is already an active pickup request for this tireset.",
                )}
              {activeRequest.delivery &&
                t(
                  "There is already an active delivery request for this tireset.",
                )}{" "}
              {activeRequest.activeRequest?.state != "concept" &&
                activeRequest.activeRequest?.state != "submitted" &&
                t("It is no longer possible to edit or cancel this request.")}
            </div>
          </div>
        </div>
      </div>
      {activeRequest.activeRequest?.stateFlow && (
        <div>
          <LayoutContent>
            <Content>
              {hasRole("super-admin") ||
                hasRole("dbh-manager") ||
                ((activeRequest.activeRequest?.state == "concept" ||
                  activeRequest.activeRequest?.state == "submitted") && (
                  <ContentHeader title={t("Request status")} className="mb-3">
                    <div className="d-flex gap-2">
                      <Button
                        variant={"dark"}
                        size={"sm"}
                        onClick={onEditingToggle}
                      >
                        {isEditingActiveRequest
                          ? t("Cancel editing")
                          : t("Edit request")}
                      </Button>
                      <Button
                        variant={"danger"}
                        size={"sm"}
                        onClick={onCancelActiveRequest}
                      >
                        {t("Cancel request")}
                      </Button>
                    </div>
                  </ContentHeader>
                ))}
              <div className="p-4">
                <RequestProgressBar
                  stateFlow={
                    activeRequest.activeRequest
                      .stateFlow as RequestProgressBarStepModel[]
                  }
                  size={"small"}
                />
              </div>
              <div className="p-4">
                <div className="request-form mt-4">
                  <div className="row g-3">
                    <div className="col-md-6">
                      <label
                        htmlFor="pickupDeliveryDate"
                        className="form-label"
                      >
                        {activeRequest.activeRequest?.type === "pickup"
                          ? t("Pickup Date")
                          : t("Delivery Date")}
                      </label>
                      <DatePicker
                        id="pickupDeliveryDate"
                        selected={
                          editingValues.pickupDeliveryDate
                            ? new Date(editingValues.pickupDeliveryDate)
                            : null
                        }
                        onChange={(date: Date | null) => {
                          if (date) {
                            const year = date.getFullYear();
                            const month = String(date.getMonth() + 1).padStart(
                              2,
                              "0",
                            );
                            const day = String(date.getDate()).padStart(2, "0");
                            onEditingValuesChange({
                              ...editingValues,
                              pickupDeliveryDate: `${year}-${month}-${day}`,
                            });
                          } else {
                            onEditingValuesChange({
                              ...editingValues,
                              pickupDeliveryDate: "",
                            });
                          }
                        }}
                        className="form-control"
                        dateFormat="dd-MM-yyyy"
                        showWeekNumbers
                        showIcon
                        calendarStartDay={1}
                        locale="nl"
                        filterDate={filterDate}
                        monthsShown={monthsShown}
                        minDate={minDate}
                        maxDate={maxDate}
                        excludeDates={excludeDates}
                        highlightDates={highlightDates}
                        disabled={!isEditingActiveRequest}
                        wrapperClassName={"form-control"}
                      />
                    </div>
                    <div className="col-md-6">
                      <label htmlFor="workOrderNumber" className="form-label">
                        {t("Work Order Number")}
                      </label>
                      <input
                        type="text"
                        className="form-control"
                        id="workOrderNumber"
                        value={editingValues.workOrderNumber || ""}
                        disabled={!isEditingActiveRequest}
                        onChange={(e) =>
                          onEditingValuesChange({
                            ...editingValues,
                            workOrderNumber: e.target.value,
                          })
                        }
                        placeholder={t("Enter work order number")}
                      />
                    </div>
                  </div>
                  {isEditingActiveRequest && (
                    <div className="mt-3">
                      <button
                        type="button"
                        className="btn btn-primary"
                        onClick={onEditRequestSubmit}
                      >
                        {requestType === "deliver"
                          ? t("Create delivery request")
                          : t("Create pickup request")}
                      </button>
                    </div>
                  )}
                </div>
              </div>
            </Content>
          </LayoutContent>
        </div>
      )}
    </div>
  );
};
